import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SignUpOrg from "@/pages/SignUpOrg";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicPack from "@/pages/PublicPack";
import WorkerDashboard from "@/pages/WorkerDashboard";
import ManagerDashboard from "@/pages/ManagerDashboard";
import ReviewQueue from "@/pages/ReviewQueue";
import WorkerDetail from "@/pages/WorkerDetail";
import TeamMembers from "@/pages/TeamMembers";
import AuditLog from "@/pages/AuditLog";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import { ShieldCheck } from "lucide-react";
import "@/App.css";

// Route ACL constants pulled to module scope so route props don't create fresh
// array refs on every render.
const STAFF_ROLES = ["team_member", "manager"];
const MANAGER_ONLY = ["manager"];

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-start bg-stone-50 px-8">
      <img
        src="/audentra-logo.png"
        alt="Audentra"
        className="h-12 w-auto animate-pulse"
      />
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user === false) nav("/login", { replace: true });
  }, [user, nav]);

  if (user === null) return <LoadingScreen />;
  if (user === false) return null;

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "worker") return <WorkerDashboard />;
  return <ManagerDashboard />;
}

function AppRouter() {
  const location = useLocation();
  // Detect Google Auth callback synchronously during render (fragment #session_id=...)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/signup" element={<SignUpOrg />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/share/pack/:token" element={<PublicPack />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workers"
        element={
          <ProtectedRoute allowedRoles={STAFF_ROLES}>
            <ManagerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workers/:id"
        element={
          <ProtectedRoute allowedRoles={STAFF_ROLES}>
            <WorkerDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review"
        element={
          <ProtectedRoute allowedRoles={STAFF_ROLES}>
            <ReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute allowedRoles={MANAGER_ONLY}>
            <TeamMembers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute allowedRoles={MANAGER_ONLY}>
            <AuditLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={MANAGER_ONLY}>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={MANAGER_ONLY}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
